import React, {useCallback, useEffect, useMemo, useState} from "react";
import {Paper} from "@mui/material";
import {Row} from "reactstrap";
import useIsMounted from "../../../../hooks/use-is-mounted";
import Api from "../../../../../core/services/api/api";
import {DataGridColumnAlignments, DataGridColumnTypes, useDataGridSavedState} from "../../../../../packages/data-grid";
import PartnerDataGrid from "../../../../containers/data-grid";
import {OrderInformationTransactionsDataGridColumnNames} from "../../../../../core/constants/table/data-grid-column-keys";
import IconButton from "../../../base/button/icon_button";
import {ReceiptLongRounded} from "@mui/icons-material";
import TransactionReceiptDialog from "../../../dialogs/transaction-receipt-dialog";
import useAccessLevels from "../../../../hooks/use-access-levels";


const _initialPaginationInfo = {
    pageSize: 10,
    currentPage: 1,
    length: 0,
    sizes: [10, 20],
};

export const OrderInformationViewTransactionsTypes = {
    order: 'UserOrder',
    invoice: 'Invoice',
}

const dataGridSaveConfig = {
    name: 'OrderInformationViewTransactionsSectionDataGrid',
    version: 2,
}

const initReceiptDialogState = {title: '', open: false, customerId: undefined, receiptId: undefined, close: () => void 0};

const OrderInformationViewTransactionsSection = ({
                                                     appliedTransactions,
                                                     loading: orderLoading,
                                                     orderId,
                                                     type,
                                                     customerId,
                                                 }) => {
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState([]);
    const [visibleData, setVisibleData] = useState([]);
    const [receiptDialogState, setReceiptDialogState] = useState(initReceiptDialogState);
    const isMounted = useIsMounted();
    const operatorAccessLevels = useAccessLevels();


    const columns = useMemo(() =>
        /** @type {DataGridColumn[]}*/
        [
            ...(operatorAccessLevels.packages.sales.paymentReceipts ? [
                {
                    name: OrderInformationTransactionsDataGridColumnNames.receipt,
                    alignment: DataGridColumnAlignments.center,
                    type: DataGridColumnTypes.element,
                    sortable: false,
                    pinned: true,
                    pinnedToggleable: false,
                    resizable: false,
                    visibilityToggleable: false,
                    reorderable: false,
                    width: 0,
                },
            ] : []),
            {
                name: OrderInformationTransactionsDataGridColumnNames.groupBy,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                sortable: false,
                pinnedToggleable: false,
                resizable: false,
                visibilityToggleable: false,
                visible: false,
                reorderable: false,
                width: 0,
            },
            {
                title: "Code",
                name: OrderInformationTransactionsDataGridColumnNames.code,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                pinned: true,
                pinnedToggleable: false,
                sortable: false,
            },
            {
                title: "Transaction No",
                name: OrderInformationTransactionsDataGridColumnNames.transactionNo,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                width: 120,
                sortable: false,
            },
            {
                title: "Date",
                name: OrderInformationTransactionsDataGridColumnNames.date,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.dateTime,
                sortable: false,
            },
            {
                title: "Debit",
                name: OrderInformationTransactionsDataGridColumnNames.debit,
                alignment: DataGridColumnAlignments.right,
                type: DataGridColumnTypes.money,
                sortable: false,
            },
            {
                title: "Credit",
                name: OrderInformationTransactionsDataGridColumnNames.credit,
                alignment: DataGridColumnAlignments.right,
                type: DataGridColumnTypes.money,
                sortable: false,
            },
            {
                title: "Authorization Code",
                name: OrderInformationTransactionsDataGridColumnNames.authCode,
                alignment: DataGridColumnAlignments.right,
                type: DataGridColumnTypes.string,
                sortable: false,
                width: 150,
            },
            {
                title: "Reference No",
                name: OrderInformationTransactionsDataGridColumnNames.referenceNo,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                sortable: false,
            },
            {
                title: "Service",
                name: OrderInformationTransactionsDataGridColumnNames.service,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                sortable: false,
            },
        ], [operatorAccessLevels.packages.sales.paymentReceipts])

    const [dataGridColumns, , dataGridPageSize, dataGridDensity] = useDataGridSavedState({
        columns: columns,
        name: dataGridSaveConfig.name,
        version: dataGridSaveConfig.version,
    })

    const [paginationInfo, setPaginationInfo] = useState(dataGridPageSize ? {
        ..._initialPaginationInfo,
        pageSize: dataGridPageSize
    } : _initialPaginationInfo);

    /**
     * Fetches the transactions of this order from the server.
     * @type {function(): Promise<void>}
     */
    const getOrderTransactions = useCallback(async () => {
        setLoading(true);
        const response = await Api.getAllTransactionsDetailsOfOrder(orderId, type);
        if (!isMounted())
            return;
        setLoading(false);
        if (response?.resultFlag) {
            setData(response.data)
        } else {
            setData([]);
        }
    }, [orderId, type, isMounted])

    /**
     * With each change in the [appliedTransaction] and [orderId] values of the props:
     * - loads the list of transactions from server.
     */
    useEffect(() => {
        void getOrderTransactions();
    }, [getOrderTransactions, appliedTransactions])

    /**
     * With each change in the [data] and [paginationInfo] values of the state:
     * - selects a slice of the data to be set as the visible data.
     */
    useEffect(() => {
        if (!data?.length) {
            setPaginationInfo(_initialPaginationInfo);
            return setVisibleData([]);
        }
        const start = Math.min(Math.max((paginationInfo.currentPage - 1) * paginationInfo.pageSize, 0), data.length);
        const end = Math.min((paginationInfo.currentPage) * paginationInfo.pageSize, data.length);
        const chunk = Array.from(data).slice(start, end)
        setPaginationInfo(prevState => ({...prevState, length: data.length}));
        setVisibleData(chunk);
    }, [data, paginationInfo.currentPage, paginationInfo.pageSize])

    const dataGridData = useMemo(() => {
        return visibleData
                ?.map(item => [
                    {
                        ...item,
                        debit: item.isDebit ? item.amount : null,
                        credit: item.isDebit ? null : item.amount,
                        groupedBy: item?.id,
                    },
                    ...(
                        item?.refundTransaction
                            ? [{
                                ...item?.refundTransaction,
                                debit: item?.refundTransaction.isDebit ? item?.refundTransaction.amount : null,
                                credit: item?.refundTransaction.isDebit ? null : item?.refundTransaction.amount,
                                groupedBy: item?.id,
                            }]
                            : []
                    )
                ])
                ?.reduce((p, c) => [...p, ...c], [])
            ?? []
    }, [visibleData])

    /**
     * Opens the receipt dialog
     * @type {(function(*): void)|*}
     */
    const openReceiptDialog = useCallback((data) => {
        setReceiptDialogState({
            title: 'Transaction Receipt',
            open: true,
            customerId: customerId,
            // TODO: receipt id for the print receipt
            receiptId: data.receiptId,
            close: () => setReceiptDialogState(initReceiptDialogState)
        });
    }, [customerId])

    const dataGridRows = useMemo(() => {
        return dataGridData?.map(item => ({
            key: item.id,
            data: item,
            cells: {
                [OrderInformationTransactionsDataGridColumnNames.groupBy]: {
                    groupValue: item.groupedBy,
                },
                [OrderInformationTransactionsDataGridColumnNames.receipt]:
                // Service e online payment.
                    item.service?.id === 2 && item.receiptId ?
                        <IconButton
                            color={'paper.primary'}
                            onPressed={() => openReceiptDialog(item)}
                        >
                            <ReceiptLongRounded className={'icon normal'}/>
                        </IconButton>
                        : ' ',
                [OrderInformationTransactionsDataGridColumnNames.code]: item.id,
                [OrderInformationTransactionsDataGridColumnNames.transactionNo]: item.transactionNo,
                [OrderInformationTransactionsDataGridColumnNames.date]: item.submittedDate,
                [OrderInformationTransactionsDataGridColumnNames.debit]: item.debit,
                [OrderInformationTransactionsDataGridColumnNames.credit]: item.credit,
                [OrderInformationTransactionsDataGridColumnNames.type]: item.transactionTypeDescription,
                [OrderInformationTransactionsDataGridColumnNames.authCode]: item.authorizationCode,
                [OrderInformationTransactionsDataGridColumnNames.referenceNo]: item.referenceDisplay,
                [OrderInformationTransactionsDataGridColumnNames.service]: item.service?.title,
            }
        }))
    }, [dataGridData, openReceiptDialog])

    return (
        <>
            <Row className='no-gutters w-100 align-items-start justify-content-center'>
                <Paper className='paper w-100 px-3 py-2'>
                    <PartnerDataGrid
                        {...dataGridSaveConfig}
                        initialLayoutState={{
                            columns: columns,
                        }}
                        hideFooterActions
                        hideExporting
                        rows={dataGridRows}
                        columns={dataGridColumns}
                        state={{
                            loading: {state: loading || orderLoading},
                            pagination: paginationInfo,
                            group: {
                                columnName: OrderInformationTransactionsDataGridColumnNames.groupBy,
                                showOnColumn: OrderInformationTransactionsDataGridColumnNames.code,
                            },
                            density: dataGridDensity,
                        }}
                        onCurrentPageChanged={(_, current) => setPaginationInfo({
                            ...current,
                            sizes: _initialPaginationInfo.sizes,
                        })}
                        onPageSizeChanged={(_, current) => setPaginationInfo({
                            ...current,
                            sizes: _initialPaginationInfo.sizes,
                        })}
                    />
                </Paper>
            </Row>
            <TransactionReceiptDialog
                title={receiptDialogState.title}
                open={receiptDialogState.open}
                customerId={receiptDialogState.customerId}
                receiptId={receiptDialogState.receiptId}
                setOpen={receiptDialogState.close}
            />
        </>
    )
}

export default OrderInformationViewTransactionsSection;
